import type { DomandeTestInput, EsitoIscritto, EsitoTest, StatoTest, Tentativo, TestAdmin } from '@aulaviva/shared';
import type { z } from 'zod';
import type { riepilogoTestSchema } from '@aulaviva/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Paginato } from './admin';
import { richiesta } from './client';

export type RiepilogoTest = z.infer<typeof riepilogoTestSchema>;

export const chiaviTest = {
  stato: (webinarId: string) => ['test', 'stato', webinarId] as const,
  tentativo: (id: string) => ['test', 'tentativo', id] as const,
  esito: (id: string) => ['test', 'esito', id] as const,
  admin: (webinarId: string) => ['test', 'admin', webinarId] as const,
  esiti: (webinarId: string, q: string, stato: string, pagina: number) => ['test', 'esiti', webinarId, q, stato, pagina] as const,
  riepilogo: (webinarId: string) => ['test', 'riepilogo', webinarId] as const,
  riepilogoRelatore: (webinarId: string) => ['test', 'riepilogo-relatore', webinarId] as const,
};

/** Risposta di PUT risposta: conferma del salvataggio con la scadenza aggiornata. */
export interface RispostaSalvata {
  risposteDate: number;
  scadenzaAt: string;
  oraServer: string;
}

// ---- partecipante
export function useStatoTest(webinarId: string, intervalloMs = 30_000) {
  return useQuery({ queryKey: chiaviTest.stato(webinarId), queryFn: async () => (await richiesta<StatoTest>(`/webinar/${webinarId}/test/stato`)).dati, refetchInterval: intervalloMs });
}
export function useAvviaTest(webinarId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await richiesta<Tentativo>(`/webinar/${webinarId}/test/avvia`, { method: 'POST' })).dati,
    onSuccess: (t) => {
      qc.setQueryData(chiaviTest.tentativo(t.id), t);
      void qc.invalidateQueries({ queryKey: chiaviTest.stato(webinarId) });
    },
  });
}
export function useTentativo(id: string) {
  return useQuery({ queryKey: chiaviTest.tentativo(id), queryFn: async () => (await richiesta<Tentativo>(`/test/${id}`)).dati, staleTime: Infinity });
}
export function useSalvaRisposta(tentativoId: string) {
  return useMutation({
    mutationFn: async (r: { domandaId: string; rispostaId: string }) => (await richiesta<RispostaSalvata>(`/test/${tentativoId}/risposta`, { method: 'PUT', json: r })).dati,
  });
}
export function useConsegnaTest(tentativoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await richiesta<EsitoTest>(`/test/${tentativoId}/consegna`, { method: 'POST' })).dati,
    onSuccess: (e) => {
      qc.setQueryData(chiaviTest.esito(tentativoId), e);
      void qc.invalidateQueries({ queryKey: ['test', 'stato'] });
      void qc.invalidateQueries({ queryKey: ['percorsi'] });
    },
  });
}
export function useEsitoTest(tentativoId: string) {
  return useQuery({ queryKey: chiaviTest.esito(tentativoId), queryFn: async () => (await richiesta<EsitoTest>(`/test/${tentativoId}/esito`)).dati });
}

// ---- admin
export function useTestAdmin(webinarId: string) {
  return useQuery({ queryKey: chiaviTest.admin(webinarId), queryFn: async () => (await richiesta<TestAdmin>(`/admin/webinar/${webinarId}/test`)).dati });
}
export function useSalvaTestAdmin(webinarId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: DomandeTestInput) => (await richiesta<TestAdmin>(`/admin/webinar/${webinarId}/test`, { method: 'PUT', json: input })).dati,
    onSuccess: (t) => qc.setQueryData(chiaviTest.admin(webinarId), t),
  });
}
export function useEsitiTest(webinarId: string, q: string, stato: string, pagina: number, perPagina = 50, intervalloMs = 10_000) {
  const p = new URLSearchParams({ pagina: String(pagina), perPagina: String(perPagina) });
  if (q) p.set('q', q);
  if (stato) p.set('stato', stato);
  return useQuery({
    queryKey: chiaviTest.esiti(webinarId, q, stato, pagina),
    queryFn: async () => (await richiesta<Paginato<EsitoIscritto>>(`/admin/webinar/${webinarId}/test/esiti?${p.toString()}`)).dati,
    refetchInterval: intervalloMs,
    placeholderData: (prec) => prec,
  });
}
export function useRiepilogoTest(webinarId: string, abilitato = true, intervalloMs = 10_000) {
  return useQuery({
    queryKey: chiaviTest.riepilogo(webinarId),
    queryFn: async () => (await richiesta<RiepilogoTest>(`/admin/webinar/${webinarId}/test/riepilogo`)).dati,
    enabled: abilitato,
    refetchInterval: intervalloMs,
  });
}
export function useChiudiTest(webinarId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await richiesta<RiepilogoTest>(`/admin/webinar/${webinarId}/test/chiudi`, { method: 'POST' })).dati,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['test'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'webinar', webinarId] });
    },
  });
}

// ---- relatore
export function useRiepilogoTestRelatore(webinarId: string, abilitato = true) {
  return useQuery({
    queryKey: chiaviTest.riepilogoRelatore(webinarId),
    queryFn: async () => (await richiesta<RiepilogoTest>(`/relatore/webinar/${webinarId}/test/riepilogo`)).dati,
    enabled: abilitato,
    refetchInterval: 30_000,
  });
}
